import React from 'react';
import { TierPlan } from './PaymentManager';
import BillingSwitcher from './BillingSwitcher';
import PlanCard from './PlanCard';

interface AvailablePlansDisplayProps {
  tierPlans: TierPlan[];
  billingInterval: 'month' | 'year';
  onBillingIntervalChange: (interval: 'month' | 'year') => void;
  onPurchase: (priceId?: string, productId?: string) => void;
  isCreatingCheckout: string | null;
  checkoutError: string | null;
  productsError: string | null;
  startFade: boolean;
  hideHeader?: boolean;
  hideFreeDebug?: boolean;
}

const AvailablePlansDisplay: React.FC<AvailablePlansDisplayProps> = ({ 
  tierPlans, 
  billingInterval, 
  onBillingIntervalChange,
  onPurchase,
  isCreatingCheckout,
  checkoutError,
  productsError,
  startFade,
  hideHeader = false,
  hideFreeDebug = false
}) => {

  // Filter out the 'Free' plan for the main columns
  const paidTierPlans = tierPlans.filter(plan => plan.tierName.toLowerCase() !== 'free');
  // Filter for the 'Free' plan(s) for debugging
  const freeTierPlans = tierPlans.filter(plan => plan.tierName.toLowerCase() === 'free');

  return (
    // Use a fragment to return multiple top-level elements
    <>
      {/* Main 3-column layout for paid plans */}
      <div className={`plans-container fade-in-content ${startFade ? 'visible' : ''}`}> 
        {/* Column 1: Header (Title + Switcher) */}
        {!hideHeader && (
          <div className="plans-header">
            <h2 className="plans-hero-title">Choose Your Plan</h2> 
            <BillingSwitcher 
              billingInterval={billingInterval} 
              onBillingIntervalChange={onBillingIntervalChange} 
            />
          </div>
        )}
        {/* End Column 1 */} 

        {/* Display product/checkout errors (Consider where these best fit in the new layout) */}
        {/* Option 1: Span across plan columns? */}
        {(checkoutError || productsError) && (
          <div style={{ gridColumn: '2 / span 2', color: 'red' }}>
            {checkoutError && <p>Error: {checkoutError}</p>}
            {productsError && <p>{productsError}</p>}
          </div>
        )}
        
        {/* Columns 2 & 3: Plan Cards */} 
        {paidTierPlans.length > 0 ? (
          paidTierPlans.map((tierPlan) => (
            <PlanCard
              key={tierPlan.tierName} // Use tierName as key
              tierPlan={tierPlan}
              billingInterval={billingInterval}
              onPurchase={onPurchase}
              isCreatingCheckout={isCreatingCheckout}
            />
          ))
        ) : (
           // Display "no products" message spanning the plan columns if no paid plans exist
           !productsError && (
             <div style={{ gridColumn: '2 / span 2', padding: '1rem' }}>
               No paid plans found.
             </div>
           )
        )}
        {/* If fewer than 2 paid plans, the grid columns will remain empty */}
      </div> 
      {/* End Main plans-container */} 

      {/* Debug Section for Free Plans */}
      {!hideFreeDebug && freeTierPlans.length > 0 && (
        <div style={{ marginTop: '32px', padding: '16px', border: '1px dashed grey' }}>
          <h4 style={{ marginTop: 0, marginBottom: '16px', color: 'grey' }}>Debug: Free Plans</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {freeTierPlans.map((tierPlan) => (
              <PlanCard
                key={tierPlan.tierName} // Use tierName as key
                tierPlan={tierPlan}
                billingInterval={billingInterval} // Pass interval, though free might not use it
                onPurchase={onPurchase} // Pass handler, might be disabled for free
                isCreatingCheckout={isCreatingCheckout}
                isFree={true} // Indicate this is the free plan card
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default AvailablePlansDisplay; 